import React, { useState } from 'react';
import { formatCurrency } from '../utils/formatters';
import api from '../services/api';

interface Transaction {
  id: number;
  date: string;
  amount: number;
  merchant_name: string | null;
  description: string;
  account_name?: string;
}

interface RulePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onConfirmFlipped?: () => void;
  ruleName: string;
  categoryName: string;
  subcategoryName?: string;
  matchingTransactions: Transaction[];
  totalMatches: number;
  totalUncategorized: number;
  alreadyCategorizedMatches: number;
  signFlipMatches?: number;
  signFlipConditions?: any[];
}

export const RulePreviewModal: React.FC<RulePreviewModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  ruleName,
  categoryName,
  subcategoryName,
  matchingTransactions,
  totalMatches,
  totalUncategorized,
  alreadyCategorizedMatches,
  signFlipMatches = 0,
  signFlipConditions,
  onConfirmFlipped,
}) => {
  const [isApplying, setIsApplying] = useState(false);
  const [flippedPreview, setFlippedPreview] = useState<{ matching_transactions: Transaction[]; total_matches: number } | null>(null);
  const [isLoadingFlipped, setIsLoadingFlipped] = useState(false);

  const handleTryFlipped = async () => {
    if (!signFlipConditions) return;
    setIsLoadingFlipped(true);
    try {
      const result = await api.transactions.previewRuleMatches({ rule_type: 'complex', conditions: signFlipConditions });
      setFlippedPreview(result);
    } catch (e) {
      console.error('Error previewing flipped rule:', e);
    } finally {
      setIsLoadingFlipped(false);
    }
  };

  const handleApplyRule = async () => {
    setIsApplying(true);
    try {
      if (flippedPreview && onConfirmFlipped) {
        await onConfirmFlipped();
      } else {
        await onConfirm();
      }
    } finally {
      setIsApplying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="rule-preview-modal">
        <div className="modal-header">
          <h2>Rule Preview</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <div className="modal-body">
          <div className="rule-summary">
            <h3>Rule: {ruleName}</h3>
            <p>
              <strong>Will categorize as:</strong> {categoryName}
              {subcategoryName && ` - ${subcategoryName}`}
            </p>
          </div>

          <div className="matches-summary">
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-number">{totalMatches}</span>
                <span className="stat-label">matching transactions</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{totalUncategorized}</span>
                <span className="stat-label">total uncategorized</span>
              </div>
            </div>
          </div>

          {totalMatches > 0 ? (
            <div className="matching-transactions">
              <h4>Transactions that will be categorized:</h4>
              <div className="transactions-list">
                {matchingTransactions.slice(0, 10).map((transaction) => (
                  <div key={transaction.id} className="transaction-item">
                    <div className="transaction-info">
                      <div className="transaction-description">
                        {transaction.merchant_name || transaction.description}
                      </div>
                      <div className="transaction-details">
                        <span className="transaction-date">
                          {new Date(transaction.date).toLocaleDateString()}
                        </span>
                        {transaction.account_name && (
                          <span className="transaction-account">
                            {transaction.account_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="transaction-amount">
                      {formatCurrency(transaction.amount)}
                    </div>
                  </div>
                ))}
                {matchingTransactions.length > 10 && (
                  <div className="more-transactions">
                    ... and {matchingTransactions.length - 10} more transactions
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="no-matches">
              {flippedPreview ? (
                <>
                  <p style={{ color: 'var(--success-color)', marginBottom: '8px' }}>
                    <strong>With corrected amounts:</strong> this rule matches {flippedPreview.total_matches} uncategorized transaction{flippedPreview.total_matches !== 1 ? 's' : ''}.
                  </p>
                  {flippedPreview.matching_transactions.slice(0, 5).map((t) => (
                    <div key={t.id} className="transaction-item" style={{ fontSize: '13px' }}>
                      <span>{t.merchant_name || t.description}</span>
                      <span>{formatCurrency(t.amount)}</span>
                    </div>
                  ))}
                  {flippedPreview.total_matches > 5 && (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                      ... and {flippedPreview.total_matches - 5} more
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p>No uncategorized transactions match this rule currently.</p>
                  {signFlipMatches > 0 && (
                    <div style={{
                      marginTop: '10px',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      background: 'rgba(234,179,8,0.1)',
                      border: '1px solid rgba(234,179,8,0.3)',
                      fontSize: '13px',
                    }}>
                      <p style={{ marginBottom: '8px' }}>
                        <strong>Tip:</strong> Your amount values look like they may be for expenses, but expenses are stored as negative numbers. With the amounts corrected, this rule would match {signFlipMatches} transaction{signFlipMatches !== 1 ? 's' : ''}.
                      </p>
                      <button
                        onClick={handleTryFlipped}
                        disabled={isLoadingFlipped}
                        className="btn btn-secondary btn-sm"
                      >
                        {isLoadingFlipped ? 'Loading…' : 'Try with corrected amounts'}
                      </button>
                    </div>
                  )}
                  {alreadyCategorizedMatches > 0 ? (
                    <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
                      However, {alreadyCategorizedMatches} already-categorized transaction{alreadyCategorizedMatches !== 1 ? 's' : ''} match — the rule will not re-categorize them.
                    </p>
                  ) : (
                    signFlipMatches === 0 && <p>The rule will be saved and applied to future transactions.</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleApplyRule}
            disabled={isApplying}
            className="btn btn-primary"
          >
            {isApplying
            ? 'Creating Rule...'
            : flippedPreview
              ? `Create Rule & Apply to ${flippedPreview.total_matches} Transaction${flippedPreview.total_matches !== 1 ? 's' : ''}`
              : totalMatches > 0
                ? `Create Rule & Apply to ${totalMatches} Transactions`
                : 'Create Rule'
          }
          </button>
        </div>
      </div>
    </div>
  );
};
